import React, { useMemo, useState } from 'react';
import { Bot, CheckCircle2, AlertTriangle, MinusCircle, X, Sparkles, CalendarRange } from 'lucide-react';
import type { ReconciliacaoResultado } from '../../../../types';
import type { SugestaoAnaliseTecnica } from './faturaToAnaliseTecnica';

// Painel de revisão do que o robô determinístico leu da fatura. Nada é aplicado
// no formulário sem o analista clicar em "Aplicar": pré-preencher em silêncio
// apagaria correção manual feita antes do upload.
//
// Três estados por campo:
//   novo        → o formulário está vazio ali, é ganho puro (marcado por padrão)
//   divergente  → já existe valor DIFERENTE (inclusive o default 'B1'/'Monofásico'
//                 que o modal chuta ao abrir) — destacado, marcado, desmarcável
//   igual       → o robô confirma o que já está lá; não vira ação, só reforço

export type EstadoCampo = 'novo' | 'divergente' | 'igual';

export interface ItemRevisao {
  campo: string;
  label: string;
  valor: string;
  atual: string;
  estado: EstadoCampo;
}

export interface AplicacaoSugestao {
  campos: Record<string, string>;
  historico: SugestaoAnaliseTecnica['historico'] | null;
  checks: Record<string, boolean>;
}

interface Props {
  sugestao: SugestaoAnaliseTecnica;
  /** valores atuais do formulário, por chave de campo */
  atuais: Record<string, string>;
  /** checklist atual do formulário. Aceita string porque o objeto de checks do modal
   *  mistura toggles (boolean) com as justificativas em texto. */
  checksAtuais: Record<string, boolean | string | undefined>;
  /** nº de linhas da grade que já têm mês preenchido (pra avisar de sobrescrita) */
  linhasHistoricoPreenchidas: number;
  reconciliacao?: ReconciliacaoResultado | null;
  onAplicar: (a: AplicacaoSugestao) => void;
  onIgnorar: () => void;
}

const classificar = (valor: string, atual: string): EstadoCampo => {
  const a = (atual || '').trim();
  if (!a) return 'novo';
  return a.toLowerCase() === valor.trim().toLowerCase() ? 'igual' : 'divergente';
};

const SugestaoFaturaPanel: React.FC<Props> = ({
  sugestao, atuais, checksAtuais, linhasHistoricoPreenchidas, reconciliacao, onAplicar, onIgnorar,
}) => {
  const itens = useMemo<ItemRevisao[]>(() => sugestao.campos.map((c) => ({
    ...c,
    atual: atuais[c.campo] ?? '',
    estado: classificar(c.valor, atuais[c.campo] ?? ''),
  })), [sugestao.campos, atuais]);

  const acionaveis = useMemo(() => itens.filter((i) => i.estado !== 'igual'), [itens]);
  const confirmados = itens.length - acionaveis.length;

  const checksAcionaveis = useMemo(
    () => sugestao.checks.filter((c) => checksAtuais[c.campo] !== c.valor),
    [sugestao.checks, checksAtuais],
  );

  // Seleção começa com tudo marcado — o caminho feliz é aceitar a leitura do robô.
  const [marcados, setMarcados] = useState<Set<string>>(() => new Set(acionaveis.map((i) => i.campo)));
  const [checksMarcados, setChecksMarcados] = useState<Set<string>>(() => new Set(checksAcionaveis.map((c) => c.campo)));
  const [aplicarHistorico, setAplicarHistorico] = useState(sugestao.historico.length > 0);

  const alternar = (set: Set<string>, chave: string, fn: (s: Set<string>) => void) => {
    const proximo = new Set(set);
    if (proximo.has(chave)) proximo.delete(chave); else proximo.add(chave);
    fn(proximo);
  };

  const total = marcados.size + checksMarcados.size + (aplicarHistorico && sugestao.historico.length ? 1 : 0);

  const aplicar = () => {
    const campos: Record<string, string> = {};
    for (const i of acionaveis) if (marcados.has(i.campo)) campos[i.campo] = i.valor;
    const checks: Record<string, boolean> = {};
    for (const c of checksAcionaveis) if (checksMarcados.has(c.campo)) checks[c.campo] = c.valor;
    onAplicar({
      campos,
      checks,
      historico: aplicarHistorico && sugestao.historico.length ? sugestao.historico : null,
    });
  };

  const badgeConfianca = () => {
    if (!reconciliacao) return null;
    const { status, score } = reconciliacao;
    const estilo = status === 'ok'
      ? 'text-emerald-700 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300'
      : status === 'revisar'
        ? 'text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300'
        : 'text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-300';
    const rotulo = status === 'ok' ? 'Confere' : status === 'revisar' ? 'Revisar' : 'Falhou';
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${estilo}`}>
        {rotulo} · {score}
      </span>
    );
  };

  return (
    <div className="mb-6 bg-white dark:bg-slate-900/80 rounded-2xl border-2 border-primary/30 dark:border-primary/25 shadow-sm overflow-hidden animate-fade-in">
      <div className="flex items-start justify-between gap-4 p-5 bg-primary/5 dark:bg-primary/10 border-b border-primary/15">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-primary/15 rounded-xl shrink-0">
            <Bot size={18} className="text-primary" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
              O robô leu a fatura
              {badgeConfianca()}
            </h4>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-0.5">
              Confira e aplique — nada é alterado até você clicar.
              {confirmados > 0 && ` ${confirmados} campo(s) já batiam com o formulário.`}
            </p>
          </div>
        </div>
        <button
          onClick={onIgnorar}
          className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all shrink-0"
          title="Ignorar a leitura do robô"
        >
          <X size={16} />
        </button>
      </div>

      <div className="p-5 space-y-4">
        {acionaveis.length === 0 && !sugestao.historico.length && checksAcionaveis.length === 0 && (
          <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
            O formulário já está de acordo com a fatura — nada a aplicar.
          </p>
        )}

        {acionaveis.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {acionaveis.map((i) => {
              const marcado = marcados.has(i.campo);
              const divergente = i.estado === 'divergente';
              return (
                <label
                  key={i.campo}
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                    divergente
                      ? 'border-amber-300 dark:border-amber-800/60 bg-amber-50/60 dark:bg-amber-900/10'
                      : 'border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/30'
                  } ${marcado ? '' : 'opacity-55'}`}
                >
                  <input
                    type="checkbox"
                    checked={marcado}
                    onChange={() => alternar(marcados, i.campo, setMarcados)}
                    className="mt-0.5 w-4 h-4 rounded accent-primary shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {divergente
                        ? <AlertTriangle size={12} className="text-amber-500 shrink-0" />
                        : <CheckCircle2 size={12} className="text-emerald-500 shrink-0" />}
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{i.label}</span>
                    </div>
                    <div className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate" title={i.valor}>
                      {i.valor}
                    </div>
                    {divergente && (
                      <div className="text-[11px] text-amber-700 dark:text-amber-400 font-medium truncate" title={i.atual}>
                        substitui: {i.atual}
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        )}

        {sugestao.historico.length > 0 && (
          <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/30 ${aplicarHistorico ? '' : 'opacity-55'}`}>
            <input
              type="checkbox"
              checked={aplicarHistorico}
              onChange={() => setAplicarHistorico((v) => !v)}
              className="mt-0.5 w-4 h-4 rounded accent-primary shrink-0"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <CalendarRange size={12} className="text-emerald-500 shrink-0" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Histórico de consumo — {sugestao.historico.length} {sugestao.historico.length === 1 ? 'mês' : 'meses'}
                </span>
              </div>
              <div className="text-sm font-semibold text-slate-700 dark:text-slate-200 truncate">
                {sugestao.historico.slice(0, 6).map((h) => `${h.month}: ${h.consumo.toLocaleString('pt-BR')}`).join(' · ')}
                {sugestao.historico.length > 6 && ' …'}
              </div>
              <div className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                Preenche mês e consumo.{' '}
                {linhasHistoricoPreenchidas > 0
                  ? `As outras colunas das ${linhasHistoricoPreenchidas} linha(s) já preenchidas são mantidas.`
                  : 'Injeção, vencimento e pagamento seguem manuais.'}
              </div>
            </div>
          </label>
        )}

        {checksAcionaveis.length > 0 && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
              <Sparkles size={11} /> Sugestões para o checklist
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {checksAcionaveis.map((c) => {
                const marcado = checksMarcados.has(c.campo);
                return (
                  <label
                    key={c.campo}
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/30 ${marcado ? '' : 'opacity-55'}`}
                  >
                    <input
                      type="checkbox"
                      checked={marcado}
                      onChange={() => alternar(checksMarcados, c.campo, setChecksMarcados)}
                      className="mt-0.5 w-4 h-4 rounded accent-primary shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] font-bold text-slate-600 dark:text-slate-300">{c.label}</div>
                      <div className={`text-sm font-bold ${c.valor ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500'}`}>
                        {c.valor ? 'Sim' : 'Não'}
                      </div>
                      <div className="text-[11px] font-medium text-slate-500 dark:text-slate-400">{c.motivo}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {sugestao.naoEncontrados.length > 0 && (
          <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1.5">
              <MinusCircle size={11} /> Segue manual — a fatura não traz
            </p>
            <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 leading-relaxed">
              {sugestao.naoEncontrados.join(' · ')}
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 px-5 py-3.5 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800">
        <button
          onClick={onIgnorar}
          className="px-4 py-2 text-sm font-bold text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-all"
        >
          Ignorar
        </button>
        <button
          onClick={aplicar}
          disabled={total === 0}
          className="px-5 py-2 text-sm font-bold text-white bg-primary rounded-xl hover:bg-primary/85 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
        >
          Aplicar {total > 0 ? `${total} item(ns)` : ''}
        </button>
      </div>
    </div>
  );
};

export default SugestaoFaturaPanel;
